import {Injectable} from '@angular/core';
import {ApiService} from "../../../../common/service/api.service";
import {ClientRelationResponse} from "../model/client-relation-response";
import {
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    ReplaySubject, switchMap,
    tap
} from "rxjs";
import {ActivatedRouteSnapshot, NavigationEnd, Router} from "@angular/router";
import {StorageService} from "../../../../core/storage/storage.service";
import {UserService} from "../../../../core/user/service/user.service";

@Injectable({
    providedIn: 'root'
})
export class ClientRelationService {
    private relationsSubject: ReplaySubject<ClientRelationResponse> =
        new ReplaySubject<ClientRelationResponse>(1);

    private activeContextSubject = new BehaviorSubject<string | null>(null);

    constructor(
        private apiService: ApiService,
        private router: Router,
        private storageService: StorageService,
        private userService: UserService,
    ) {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.getContext(this.router.routerState.snapshot.root)),
            distinctUntilChanged()
        ).subscribe(this.activeContextSubject);
    }

    private getContext(route: ActivatedRouteSnapshot): string | null {
        let current = route;
        while (current.firstChild) {
            current = current.firstChild;
        }
        return current.data['context'] || null;
    }

    loadRelations(): Observable<ClientRelationResponse | null> {
        const user = this.userService.user;

        if (this.userService.adminCheck(user)) {
            return of(null);
        } else {
            const cached = this.storageService.clientRelations;
            if (cached) {
                this.relationsSubject.next(cached);
                return of(cached);
            }

            if (this.storageService.isClientMode()) {
                return this.loadRelationsFromApi('/admin/relations/' + user.externalId);
            }

            if (!this.storageService.isClientMode()) {
                return this.loadRelationsFromApi('/relations');
            }
        }
    }

    getChecked(): Observable<number[]> {
        return combineLatest([
            this.relationsSubject.asObservable(),
            this.activeContextSubject.asObservable()
        ]).pipe(
            filter(([_, context]) => context === this.getContext(this.router.routerState.snapshot.root)),
            map(([relations]) => {
                return this.getClientIds(relations);
            }),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
        );
    }

    getRelations(): Observable<ClientRelationResponse> {
        return this.relationsSubject.asObservable();
    }

    getClientIds(relations: ClientRelationResponse) {
        const ids = [];
        if (relations.client.enabled) {
            ids.push(relations.client.id);
        }
        relations.relatedClients.forEach(relation => {
            if (relation.enabled) {
                ids.push(relation.id);
            }
        });
        return ids;
    }

    updateRelations(clientRelations: ClientRelationResponse): void {
        this.storageService.clientRelations = clientRelations;
        this.relationsSubject.next(clientRelations);
    }

    loadRelationsFromApi(endpoint: string): Observable<ClientRelationResponse> {
        return this.apiService
            .get<ClientRelationResponse>(endpoint)
            .pipe(
                tap((relations) => {
                    relations.client.enabled = true;
                    relations.relatedClients.forEach(relation => relation.enabled = false);

                    localStorage.setItem('clientRelations', JSON.stringify(relations));
                    this.relationsSubject.next(relations);
                })
            )
    }
}
